import {
  AppConfig,
  HiddenSectionsProvider,
  LLMProvider,
} from '@ltvco/refresh-lib/ctx';
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Grid } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  useAccount,
  EtagCTA,
  generateVehicleSpecs,
  getPremiumRecordsFound,
  HideableSection,
  NotesSection,
  ReportLoading,
  ReportRating,
  statesContact,
  UpgradePremiumDataModal,
  useFreeUpgradeMutation,
  UserReportPreferencesModal,
  vehicleComplaints,
  VehicleDetailedHistory,
  VehicleImpounds,
  VehicleLiens,
  VehicleReport,
  VehicleReportDisclaimer,
  VehicleSpecs,
  VehicleTitleBrands,
  VehicleTitleRecords,
} from '@ltvco/refresh-lib/v1';
import {
  VehicleDetailedHistorySection as VehicleDetailedHistoryV2Section,
  VehicleDamageHistorySection,
  VehicleTitleSummarySection,
  VehiclePremiumSection,
  VehicleSmartInsightsSection,
  VehicleSalesSection,
  VehicleReportOverview,
  VehicleDamageHistoryV2Section,
  VehicleSpecsSection as VehicleSpecsV2Section,
  VehicleMaintenanceScheduleSection,
  VehicleOwnershipCostSection,
  VehicleAuctionsSection,
  VehicleRecallsSection,
  VehicleSafetyRatingsSection,
  VehicleServiceRepairsSection,
  VehicleTheftsSection,
  VehicleWarrantySection,
  VehicleOwnershipHistorySection,
  VehicleExportsSection,
  VehicleSafetyComplaintsSection,
  VehicleInspectionsSection,
  VehicleTrimSelect,
  ReportNullState,
  RecordsFoundModal,
} from '@ltvco/refresh-lib/v2';
import {
  useQuery,
  FeaturesReady,
  useFeatureIsOn,
} from '@ltvco/refresh-lib/vendors';
import { useEffect, useMemo, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { ReportProps } from 'components/ChatBV/types';
import { getEmulatedUseQueryResult } from 'components/ChatBV/utils';
import { getInitialVehicleReportPreferences } from 'components/reports/Preferences/getInitialVehicleReportPreferences';
import { VehicleValueSection } from 'components/VehicleValueSection/VehicleValueSection';

const UseVehicleComplaints = (year: string, make: string, model: string) => {
  const { logError } = useContext(AppConfig);
  const result = useQuery<any>({
    queryKey: ['vehicleComplaints'],
    queryFn: () => {
      return vehicleComplaints(year, make, model);
    },

    enabled: Boolean(year) && Boolean(make) && Boolean(model),
    onError: (error) => {
      logError('Error fetching vehicle complaints', error as Error);
    },
  });

  return { ...result };
};

const UseStatesContact = () => {
  const { logError } = useContext(AppConfig);
  const result = useQuery<any>({
    queryKey: ['statesContact'],
    queryFn: () => {
      return statesContact();
    },

    onError: (error) => {
      logError('Error fetching vehicle complaints', error as Error);
    },
  });

  return { ...result };
};

export function VehicleChatBvReport({
  permalink,
  reportData: vehicleReportData,
}: ReportProps) {
  const apiQueryResult = useReport(permalink, undefined, 'vehicle', {
    enabled: !vehicleReportData,
  });
  const queryResult = vehicleReportData
    ? getEmulatedUseQueryResult(vehicleReportData, true, false, false)
    : apiQueryResult;
  const isInDetailedHistoryVariation = useFeatureIsOn('bump-4397');
  const isInSummaryCardsVariation = useFeatureIsOn('bump-4414');
  const isInDamageHistory = useFeatureIsOn('bump-4464');

  const isInVehicleSpecsVariation = useFeatureIsOn('bump-4461');

  const { data: accountData } = useAccount(true);
  const [preferencesModalVisible, setPreferencesModalVisible] = useState(false);
  const initialVehicleReportPreferences = getInitialVehicleReportPreferences();
  const adjustedInitialVehicleReportPreferences = isInDamageHistory
    ? [
        ...initialVehicleReportPreferences.filter(
          (item) => item.navSection !== 'Damage History'
        ),
        {
          navSection: 'Damage History',
          sections: [
            {
              nameToDisplay: 'Accidents and Damages',
              nameToRecord: 'accidents_and_damages',
              checked: false,
            },
            {
              nameToDisplay: 'Repair Quotes',
              nameToRecord: 'repair_quotes',
              checked: false,
            },
            {
              nameToDisplay: 'Salvage Records',
              nameToRecord: 'salvage_records',
              checked: false,
            },
          ],
        },
      ]
    : initialVehicleReportPreferences;

  const [sectionsToHide, setSectionsToHide] = useState(
    adjustedInitialVehicleReportPreferences
  );

  const hiddenSections =
    accountData?.account?.user_report_preferences.hidden_sections
      .vehicle_report || [];

  useEffect(() => {
    if (hiddenSections) {
      setSectionsToHide((prevSections) =>
        prevSections.map((navSection) => ({
          ...navSection,
          sections: navSection.sections.map((section) => ({
            ...section,
            checked: !hiddenSections.includes(section.nameToRecord),
          })),
        }))
      );
    }
  }, [accountData]);

  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult as any);

  const zeroed = isZeroed(queryResult as any);
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [showAdditionalRecordsModal, setShowAdditionalRecordsModal] =
    useState(false);
  const [premiumRecordsFound, setPremiumRecordsFound] = useState<string[]>([]);
  const [upgradeReportCalled, setUpgradeReportCalled] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const { data } = UseVehicleComplaints(year, make, model);
  const upgradeReport = useFreeUpgradeMutation(permalink);
  const { data: statesContactInfo } = UseStatesContact();

  const report = useMemo(() => {
    if (
      !queryResult.isLoading &&
      !queryResult.isError &&
      queryResult.data &&
      !queryResult.isLoading
    ) {
      const newReport = new VehicleReport(queryResult.data as any);
      const reportData = newReport.data;

      if (reportData.automobiles) {
        const newYear = reportData.automobiles[0]?.year?.toString() || '';
        const newMake = reportData.automobiles[0]?.make || '';
        const newModel = reportData.automobiles[0]?.model || '';
        const newReportTitle =
          reportData.automobiles[0]?.vehicleYMM?.toUpperCase() ||
          reportData.meta?.metaName ||
          '';

        if (newYear !== year) {
          setYear(newYear);
        }
        if (newMake !== make) {
          setMake(newMake);
        }
        if (newModel !== model) {
          setModel(newModel);
        }
        if (newReportTitle !== reportTitle) {
          setReportTitle(newReportTitle);
        }

        document.title = `${newReportTitle || 'Search'} - Beenverified`;
      }

      return newReport;
    }

    return null;
  }, [queryResult, year, make, model, reportTitle]);

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={10} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  if (!report && (upgradeReport.isSuccess || upgradeReport.isLoading)) {
    return <UpgradePremiumDataModal />;
  }

  if (!report) {
    return <ReportLoading menuItems={10} />;
  }
  const reportData = report.data;

  if (reportData.automobiles.length === 0) {
    return <ReportNullState />;
  }

  const vehicleSpecs = generateVehicleSpecs(reportData);

  const isYMMSearch = reportData.meta?.search_by === 'ymm';
  const ymmPresent = [year, make, model].filter(Boolean);
  const hasAllYMMPresent = ymmPresent.length === 3;
  const ymm = ymmPresent.join(' ');

  const handleFreeUpgrade = () => {
    upgradeReport.mutate({ permalink });
    setUpgradeReportCalled(true);
  };

  const handleOnClickPremiumData = () => {
    setShowAdditionalRecordsModal(!showAdditionalRecordsModal);
    setUpgradeReportCalled(!upgradeReportCalled);
  };

  const handlePreferencesModalVisibility = () => {
    setPreferencesModalVisible(!preferencesModalVisible);
  };

  if (
    report &&
    upgradeReportCalled &&
    upgradeReport.isSuccess &&
    !showAdditionalRecordsModal
  ) {
    setPremiumRecordsFound(getPremiumRecordsFound(reportData));
    setShowAdditionalRecordsModal(true);
  }

  const isUpgradedAndSuccess =
    reportData.report_upgraded && reportData.automobiles[0]?.nmvtis_success;

  const damageHistoryProps = {
    vehicleAccidentsList: reportData?.automobiles?.[0]?.accidents,
    vehicleRecallsList: reportData?.automobiles?.[0]?.recalls,
    vehicleServiceRepairsList: reportData?.automobiles?.[0]?.service_repairs,
    vehicleTheftsList: reportData?.automobiles?.[0]?.thefts,
    vehicleLiensList: reportData?.automobiles?.[0]?.liens,
    vehicleImpoundsList: reportData?.automobiles?.[0]?.impounds,
    vehicleTitleBrandsList: reportData?.automobiles?.[0]?.title_checks,
    vehicleTitleRecordsList: reportData?.automobiles?.[0]?.title_registrations,
    vehicleAuctionsDamageList: reportData?.automobiles?.[0]?.auctions,
    vehicleSalvagesList: reportData?.automobiles?.[0]?.salvage,
    vehicleRepairQuotesList:
      reportData?.automobiles?.[0]?.damage_history?.repair_quotes,
    nmvtisSuccess: reportData?.automobiles?.[0]?.nmvtis_success || false,
    reportUpgraded: reportData.report_upgraded,
    handleFreeUpgrade,
  };

  return (
    <HiddenSectionsProvider reportType="vehicle_report">
      <LLMProvider llmData={reportData.automobiles[0]} llmType="vehicle">
        {upgradeReport.isLoading && <UpgradePremiumDataModal />}
        <RecordsFoundModal
          recordsFound={premiumRecordsFound}
          open={showAdditionalRecordsModal}
          handleClose={handleOnClickPremiumData}
        />
        <Grid container direction={'row'} columns={12} spacing={7}>
          <Grid
            marginTop={{
              xs: 0,
              md: 0.5,
            }}
            sx={{
              '&.MuiGrid-item': {
                paddingTop: { xs: 3.75, md: 7 },
              },
            }}
          >
            {isYMMSearch && (
              <VehicleTrimSelect year={year} make={make} model={model} />
            )}
            <VehicleReportOverview
              reportType="vehicle-report"
              vehicleReportData={reportData}
              permalink={permalink}
              nmvtisSuccess={reportData.automobiles[0]?.nmvtis_success}
              reportUpgraded={reportData.report_upgraded}
              handleFreeUpgrade={handleFreeUpgrade}
              hasBadge={false}
            />
            {!isYMMSearch && hasAllYMMPresent && (
              <HideableSection
                nameToDisplay="Smart Insights"
                nameToRecord="smart_insights"
                id="smart-insights"
              >
                <Box mb={2.5}>
                  <VehicleSmartInsightsSection reportData={reportData} />
                </Box>
              </HideableSection>
            )}
            <>
              {!isYMMSearch && (
                <>
                  <HideableSection
                    nameToDisplay="Ownership History"
                    nameToRecord="ownership_history"
                    id="vehicle-ownership-history"
                  >
                    <VehicleOwnershipHistorySection
                      vehicleOwnershipHistories={
                        reportData.automobiles[0]?.ownership_history
                      }
                    />
                  </HideableSection>
                  {isInSummaryCardsVariation ? (
                    <>
                      <HideableSection
                        nameToDisplay="Title Summary"
                        nameToRecord="title_summary"
                        id="vehicle-title-summary"
                      >
                        <VehicleTitleSummarySection
                          vehicleTitleRegistrationsList={
                            reportData?.automobiles?.[0]?.title_registrations
                          }
                          vehicleTitleBrandsList={
                            reportData?.automobiles?.[0]?.title_checks
                          }
                          vehicleLiensList={reportData?.automobiles?.[0]?.liens}
                          vehicleImpoundList={
                            reportData?.automobiles?.[0]?.impounds
                          }
                          statesContact={statesContactInfo?.states_contact}
                          nmvtisSuccess={
                            reportData?.automobiles?.[0]?.nmvtis_success ||
                            false
                          }
                          reportUpgraded={reportData.report_upgraded}
                          handleFreeUpgrade={handleFreeUpgrade}
                        />
                      </HideableSection>
                      <EtagCTA />
                    </>
                  ) : (
                    <>
                      <HideableSection
                        nameToDisplay="Title Records"
                        nameToRecord="title_records"
                        id="vehicle-title-records"
                      >
                        {isUpgradedAndSuccess ? (
                          <VehicleTitleRecords
                            vehicleTitleRegistrationsList={
                              reportData.automobiles[0]?.title_registrations
                            }
                            statesContact={
                              statesContactInfo?.states_contact || []
                            }
                            nmvtisSuccess={
                              reportData.automobiles[0]?.nmvtis_success || false
                            }
                            reportUpgraded={reportData.report_upgraded}
                            handleFreeUpgrade={handleFreeUpgrade}
                          />
                        ) : (
                          <VehiclePremiumSection
                            showTopBar
                            sectionId="vehicle-title-records"
                            sectionName="Title Records"
                            nmvtisSuccess={
                              reportData.automobiles[0]?.nmvtis_success
                            }
                            reportUpgraded={reportData.report_upgraded}
                            handleFreeUpgrade={handleFreeUpgrade}
                          />
                        )}
                      </HideableSection>
                      <EtagCTA />
                      <HideableSection
                        nameToDisplay="Title Brands"
                        nameToRecord="title_brands"
                        id="vehicle-title-brands"
                      >
                        {isUpgradedAndSuccess ? (
                          <VehicleTitleBrands
                            vehicleTitleBrandsList={
                              reportData.automobiles[0]?.title_checks
                            }
                            statesContact={
                              statesContactInfo?.states_contact || []
                            }
                          />
                        ) : (
                          <VehiclePremiumSection
                            showTopBar
                            sectionId="vehicle-title-brands"
                            sectionName="Title Brands"
                            nmvtisSuccess={
                              reportData.automobiles[0]?.nmvtis_success
                            }
                            reportUpgraded={reportData.report_upgraded}
                            handleFreeUpgrade={handleFreeUpgrade}
                          />
                        )}
                      </HideableSection>
                      <HideableSection
                        nameToDisplay="Lien Records"
                        nameToRecord="lien_records"
                        id="vehicle-liens-report-section"
                      >
                        <VehicleLiens
                          vehicleLiensList={reportData.automobiles[0]?.liens}
                        />
                      </HideableSection>
                      <HideableSection
                        nameToDisplay="Impound Records"
                        nameToRecord="impound_records"
                        id="vehicle-impound"
                      >
                        <VehicleImpounds
                          vehicleImpoundList={
                            reportData.automobiles[0]?.impounds
                          }
                        />
                      </HideableSection>
                    </>
                  )}
                  <HideableSection
                    nameToDisplay="Export Records"
                    nameToRecord="export_records"
                    id="vehicle-exports"
                  >
                    <VehicleExportsSection
                      vehicleExportList={reportData.automobiles[0]?.exports}
                    />
                  </HideableSection>
                </>
              )}
              <HideableSection
                nameToDisplay="Specs"
                nameToRecord="specifications"
                id="vehicle-specs"
              >
                {isInVehicleSpecsVariation ? (
                  <VehicleSpecsV2Section
                    vehicleSpecs={vehicleSpecs}
                    vehicleDesign={reportData.automobiles[0]?.design}
                    firstVehicleImage={reportData.automobiles[0]?.images?.[0]}
                  />
                ) : (
                  <VehicleSpecs
                    vehicleSpecs={vehicleSpecs}
                    vehicleDesign={reportData.automobiles[0]?.design}
                  />
                )}
              </HideableSection>
            </>

            {!isYMMSearch && (
              <>
                <FeaturesReady>
                  {reportData.automobiles[0]?.detailedHistory && (
                    <HideableSection
                      nameToDisplay="Detailed History"
                      nameToRecord="detailed_history"
                      id="detailed-history"
                    >
                      {isInDetailedHistoryVariation ? (
                        <VehicleDetailedHistoryV2Section
                          detailedHistory={
                            reportData.automobiles[0]?.detailedHistory
                          }
                          ymm={ymm}
                        />
                      ) : (
                        <VehicleDetailedHistory
                          detailedHistory={
                            reportData.automobiles[0]?.detailedHistory
                          }
                          ymm={ymm}
                        />
                      )}
                    </HideableSection>
                  )}
                </FeaturesReady>

                <HideableSection
                  nameToDisplay="Sale Listings"
                  nameToRecord="sales"
                  id="vehicle-sales"
                >
                  <VehicleSalesSection
                    vehicleSalesList={reportData.automobiles[0]?.sales}
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Auctions"
                  nameToRecord="auctions"
                  id="vehicle-auctions"
                >
                  <VehicleAuctionsSection
                    vehicleAuctionsList={reportData.automobiles[0]?.auctions}
                    vehicleYMM={reportData.automobiles[0]?.vehicleYMM || ''}
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Damage History"
                  nameToRecord="damage_history"
                  id="vehicle-damage-history"
                >
                  {isInDamageHistory ? (
                    <VehicleDamageHistoryV2Section {...damageHistoryProps} />
                  ) : (
                    <VehicleDamageHistorySection {...damageHistoryProps} />
                  )}
                </HideableSection>
                <HideableSection
                  nameToDisplay="Theft Records"
                  nameToRecord="theft_records"
                  id="vehicle-thefts"
                >
                  <VehicleTheftsSection
                    vehicleTheftsList={reportData.automobiles[0]?.thefts}
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Service and Repairs"
                  nameToRecord="service_and_repairs"
                  id="vehicle-service-and-repairs"
                >
                  <VehicleServiceRepairsSection
                    repairs={reportData.automobiles[0]?.repairs || []}
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Inspections"
                  nameToRecord="inspections"
                  id="vehicle-inspections"
                >
                  <VehicleInspectionsSection
                    inspectionEvents={
                      reportData.automobiles[0]?.inspection_events
                    }
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Maintenance Schedule"
                  nameToRecord="maintenance_schedule"
                  id="vehicle-maintenance"
                >
                  <VehicleMaintenanceScheduleSection
                    vehicleMaintenanceSchedules={
                      reportData.automobiles[0]?.maintenances || []
                    }
                  />
                </HideableSection>
              </>
            )}
            <HideableSection
              nameToDisplay="Recalls"
              nameToRecord="recalls"
              id="vehicle-recalls"
            >
              <VehicleRecallsSection
                vehicleRecallList={[
                  ...(reportData.automobiles[0]?.openRecalls ?? []),
                  ...(isYMMSearch
                    ? reportData.automobiles[0]?.otherRecalls ?? []
                    : reportData.automobiles[0]?.closedRecalls ?? []),
                ]}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Market Value"
              nameToRecord="market_value"
              id="vehicle-market"
            >
              <VehicleValueSection vehicleReport={reportData} />
            </HideableSection>
            <HideableSection
              nameToDisplay="Ownership Costs"
              nameToRecord="ownership_costs"
              id="vehicle-ownership-cost"
            >
              <VehicleOwnershipCostSection
                vehicleOwnershipCosts={
                  reportData.automobiles[0]?.ownership_costs
                }
                vehicleYMM={reportData.automobiles[0]?.vehicleYMM || ''}
              />
            </HideableSection>
            <HideableSection
              nameToDisplay="Warranties"
              nameToRecord="warranties"
              id="vehicle-warranty"
            >
              <VehicleWarrantySection
                vehicleWarrantyList={reportData.automobiles[0]?.warranties}
              />
            </HideableSection>
            <HideableSection
              nameToDisplay="Vehicle Safety"
              nameToRecord="vehicle_safety"
              id="vehicle-warranty"
            >
              <VehicleSafetyRatingsSection
                vehicleSafetyRatingsList={
                  reportData.automobiles[0]?.safety_ratings || []
                }
                vehicleYMM={reportData.automobiles[0]?.vehicleYMM || ''}
                complaintsCount={data?.complaints?.length || 0}
              />
              <VehicleSafetyComplaintsSection
                ymm={reportData.automobiles[0]?.vehicleYMM || ''}
                complaints={data?.complaints}
              />
            </HideableSection>
            <NotesSection permalink={permalink} />
            <ReportRating
              permalink={permalink}
              rating={reportData.meta?.rating || null}
            />
            <VehicleReportDisclaimer />
          </Grid>
        </Grid>
        {preferencesModalVisible && (
          <UserReportPreferencesModal
            sectionsToHide={sectionsToHide}
            handleOnClose={handlePreferencesModalVisibility}
          />
        )}
      </LLMProvider>
    </HiddenSectionsProvider>
  );
}
